/**
 * Features:
 * - Anonymous visitor tracking with persistent pseudo_id
 * - First-party ad attribution (Facebook, Google, TikTok)
 * - Identity stitching (anonymous → customer)
 * - Intelligent checkout behavior detection
 * - GA4 integration with custom dimensions
 * - BigQuery export ready
 * 
 */

(function () {
    'use strict';
  
    // ============================================
    // CONFIGURATION
    // ============================================
  
    const CONFIG = {
      // Storage keys
      localStorageKey: 'visitor_uid',
      cookieName: 'visitor_uid',
      cookieExpireDays: 365,
      sessionStorageKey: 'visitor_uid_session',
      attributionKey: 'first_touch_attribution',
      attributionExpireDays: 30,
      behaviorCacheKey: 'shopify_checkout_behavior',
      trackedOrdersKey: 'tracked_orders',
  
      // GA4 Configuration
      ga4MeasurementId: 'G-JXGKF7HK15', // REPLACE WITH YOUR GA4 MEASUREMENT ID
  
      // Debug mode (set to false in production)
      debug: false,
    };
  
    // ============================================
    // UTILITY FUNCTIONS
    // ============================================
  
    const utils = {
      /**
       * Generate unique visitor ID
       * Format: uid_[timestamp]_[random]
       */
      generateId: () => `uid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  
      /**
       * Simple hash function for email privacy
       */
      hash: (text) => {
        if (!text) return null;
        let hash = 0;
        for (let i = 0; i < text.length; i++) {
          hash = ((hash << 5) - hash) + text.charCodeAt(i);
          hash |= 0;
        }
        return 'h_' + Math.abs(hash).toString(36);
      },
  
      /**
       * Safe localStorage/sessionStorage access
       */
      storage: {
        get: (key, storage = 'local') => {
          try {
            const store = storage === 'local' ? localStorage : sessionStorage;
            return store.getItem(key);
          } catch (e) {
            utils.log('Storage unavailable:', e.message);
            return null;
          }
        },
  
        set: (key, value, storage = 'local') => {
          try {
            const store = storage === 'local' ? localStorage : sessionStorage;
            store.setItem(key, value);
            return true;
          } catch (e) {
            utils.log('Storage unavailable:', e.message);
            return false;
          }
        }
      },
  
      /**
       * Cookie management
       */
      cookie: {
        get: (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          return parts.length === 2 ? parts.pop().split(';').shift() : null;
        },
  
        set: (name, value, days) => {
          const date = new Date();
          date.setTime(date.getTime() + (days * 864e5));
          document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/;SameSite=Lax`;
        }
      },
  
      /**
       * Debug logging
       */
      log: (...args) => {
        if (CONFIG.debug) {
          console.log('[Visitor Tracking]', ...args);
        }
      }
    };
  
    // ============================================
    // PSEUDO ID MANAGEMENT
    // ============================================
  
    const PseudoID = {
      /**
       * Get or create pseudo_id
       * Priority: localStorage > cookie > sessionStorage > generate new
       */
      get: () => {
        let id = utils.storage.get(CONFIG.localStorageKey) ||
          utils.cookie.get(CONFIG.cookieName) ||
          utils.storage.get(CONFIG.sessionStorageKey, 'session');
  
        if (!id) {
          id = utils.generateId();
          PseudoID.save(id);
          utils.log('New pseudo_id generated:', id);
        } else {
          // Ensure cookie exists (sync across storage)
          if (!utils.cookie.get(CONFIG.cookieName)) {
            utils.cookie.set(CONFIG.cookieName, id, CONFIG.cookieExpireDays);
          }
          utils.log('Existing pseudo_id loaded:', id);
        }
  
        return id;
      },
  
      /**
       * Save pseudo_id to all storage methods
       */
      save: (id) => {
        utils.storage.set(CONFIG.localStorageKey, id);
        utils.cookie.set(CONFIG.cookieName, id, CONFIG.cookieExpireDays);
        utils.storage.set(CONFIG.sessionStorageKey, id, 'session');
      }
    };
  
    // ============================================
    // ATTRIBUTION TRACKING
    // ============================================
  
    const Attribution = {
      /**
       * Capture attribution from URL parameters
       */
      capture: () => {
        const params = new URLSearchParams(window.location.search);
        const hasAdParams = params.has('fbclid') || params.has('gclid') ||
          params.has('ttclid') || params.has('utm_source');
  
        if (!hasAdParams) return null;
  
        const attribution = {
          timestamp: new Date().toISOString(),
          landing_page: window.location.href,
          referrer: document.referrer || 'direct',
  
          // Click IDs
          fbclid: params.get('fbclid'),
          gclid: params.get('gclid'),
          ttclid: params.get('ttclid'),
  
          // UTM parameters
          utm_source: params.get('utm_source'),
          utm_medium: params.get('utm_medium'),
          utm_campaign: params.get('utm_campaign'),
          utm_term: params.get('utm_term'),
          utm_content: params.get('utm_content'),
          utm_id: params.get('utm_id'),
  
          // Primary click ID
          click_id: params.get('fbclid') || params.get('gclid') || params.get('ttclid')
        };
  
        utils.log('Attribution captured:', attribution);
        return attribution;
      },
  
      /**
       * Store first-touch attribution (only if not already stored)
       */
      storeFirstTouch: (attribution) => {
        if (!attribution || utils.storage.get(CONFIG.attributionKey)) return;
  
        const data = JSON.stringify(attribution);
        utils.storage.set(CONFIG.attributionKey, data);
        utils.cookie.set(CONFIG.attributionKey, data, CONFIG.attributionExpireDays);
        utils.log('First-touch attribution stored');
      },
  
      /**
       * Get first-touch attribution
       */
      getFirstTouch: () => {
        try {
          const data = utils.storage.get(CONFIG.attributionKey) ||
            utils.cookie.get(CONFIG.attributionKey);
          return data ? JSON.parse(data) : null;
        } catch (e) {
          utils.log('Error parsing attribution:', e.message);
          return null;
        }
      }
    };
  
    // ============================================
    // GA4 INTEGRATION
    // ============================================
  
    const GA4 = {
      /**
       * Configure GA4 with pseudo_id
       * CRITICAL: Ensures pseudo_id is sent with ALL events for BigQuery export
       */
      configure: (pseudoId) => {
        if (typeof gtag !== 'function') {
          utils.log('GA4 not available, will retry...');
          return false;
        }
  
        // Get first-touch attribution for default parameters
        const firstTouch = Attribution.getFirstTouch();
  
        // CRITICAL FIX: Configure GA4 to send pseudo_id with EVERY event
        // This ensures BigQuery receives pseudo_id in event_params array
        gtag('config', CONFIG.ga4MeasurementId, {
          // Send these parameters with ALL events
          send_page_view: true,
          pseudo_id: pseudoId,
          first_touch_source: firstTouch?.utm_source || null,
          first_touch_campaign: firstTouch?.utm_campaign || null,
          first_touch_medium: firstTouch?.utm_medium || null,
          first_click_id: firstTouch?.click_id || null
        });
  
        // Also set as user properties (for GA4 UI reporting)
        gtag('set', 'user_properties', {
          pseudo_id: pseudoId,
          first_touch_source: firstTouch?.utm_source || null,
          first_touch_campaign: firstTouch?.utm_campaign || null
        });
  
        // Send initialization event
        gtag('event', 'pseudo_id_initialized', {
          pseudo_id: pseudoId,
          first_touch_source: firstTouch?.utm_source || null,
          first_touch_campaign: firstTouch?.utm_campaign || null,
          first_touch_medium: firstTouch?.utm_medium || null,
          first_click_id: firstTouch?.click_id || null,
          timestamp: new Date().toISOString()
        });
  
        // Capture GA4's client_id for cross-reference
        gtag('get', CONFIG.ga4MeasurementId, 'client_id', (clientId) => {
          if (clientId) {
            utils.storage.set('ga4_client_id', clientId);
            utils.log('GA4 client_id captured:', clientId);
          }
        });
  
        utils.log('✅ GA4 configured with pseudo_id for ALL events:', pseudoId);
        return true;
      },
  
      /**
       * Send custom event to GA4
       * Ensures all custom parameters are included
       */
      sendEvent: (eventName, params = {}) => {
        if (typeof gtag !== 'function') {
          utils.log('GA4 not available, event not sent:', eventName);
          return;
        }
  
        const firstTouch = Attribution.getFirstTouch();
        const pseudoId = window.TRACKING_DATA?.pseudo_id;
  
        // Always include pseudo_id and attribution with every event
        gtag('event', eventName, {
          pseudo_id: pseudoId,
          first_touch_source: firstTouch?.utm_source || null,
          first_touch_campaign: firstTouch?.utm_campaign || null,
          first_touch_medium: firstTouch?.utm_medium || null,
          first_click_id: firstTouch?.click_id || null,
          timestamp: new Date().toISOString(),
          ...params  // Custom params override defaults
        });
  
        utils.log('GA4 event sent:', eventName, params);
      }
    };
  
    // ============================================
    // CHECKOUT BEHAVIOR DETECTION
    // ============================================
  
    const BehaviorDetector = {
      /**
       * Detect Shopify checkout behavior
       * Returns: standard_redirect | order_status | ajax_success | custom_thankyou | unknown
       */
      detect: () => {
        // Check cache first (30-day TTL)
        const cached = utils.storage.get(CONFIG.behaviorCacheKey);
        if (cached) {
          try {
            const data = JSON.parse(cached);
            if (data.timestamp > Date.now() - 2592e6) { // 30 days in ms
              utils.log('Using cached behavior:', data.behavior);
              return data.behavior;
            }
          } catch (e) {
            utils.log('Invalid cache, re-detecting');
          }
        }
  
        // Run detection checks
        const checks = {
          standard_redirect: BehaviorDetector.checkStandard(),
          order_status: BehaviorDetector.checkOrderStatus(),
          ajax_success: BehaviorDetector.checkAjax(),
          custom_thankyou: BehaviorDetector.checkCustom()
        };
  
        // Find highest confidence
        let behavior = 'unknown';
        let maxConf = 0;
  
        for (const [key, conf] of Object.entries(checks)) {
          if (conf > maxConf) {
            maxConf = conf;
            behavior = key;
          }
        }
  
        // Cache result
        utils.storage.set(CONFIG.behaviorCacheKey, JSON.stringify({
          behavior,
          confidence: maxConf,
          timestamp: Date.now()
        }));
  
        utils.log('Detected behavior:', behavior, `(${maxConf}% confidence)`);
        return behavior;
      },
  
      /**
       * Check for standard Shopify redirect to /thank_you
       */
      checkStandard: () => {
        let c = 0;
        if (window.location.pathname.includes('/thank')) c += 40;
        if (window.Shopify?.checkout) c += 40;
        if (document.querySelector('.os-order-number, [data-step="thank_you"]')) c += 20;
        return c;
      },
  
      /**
       * Check for order status page redirect
       */
      checkOrderStatus: () => {
        let c = 0;
        if (window.location.pathname.match(/\/orders\/[a-zA-Z0-9]+/)) c += 50;
        if (document.querySelector('.order-status')) c += 30;
        if (window.Shopify?.order) c += 20;
        return c;
      },
  
      /**
       * Check for AJAX success (no redirect)
       */
      checkAjax: () => {
        let c = 0;
        if (window.location.pathname.includes('/checkout')) c += 20;
        if (document.querySelectorAll('.order-confirmation, .checkout-success, .thank-you-message').length) c += 50;
        if (document.querySelector('[data-ajax-checkout]')) c += 30;
        return c;
      },
  
      /**
       * Check for custom thank you page
       */
      checkCustom: () => {
        let c = 0;
        const patterns = ['/pages/thank-you', '/success', '/order-received'];
        if (patterns.some(p => window.location.pathname.includes(p))) c += 50;
        const params = new URLSearchParams(window.location.search);
        if (params.has('order_id') || params.has('order')) c += 50;
        return c;
      },
  
      /**
       * Get tracking strategy based on detected behavior
       */
      getStrategy: (behavior) => {
        const strategies = {
          standard_redirect: {
            primary: 'thankYou',
            fallback: ['success', 'observer'],
            wait: 0,
            retry: 1000
          },
          order_status: {
            primary: 'orderStatus',
            fallback: ['thankYou', 'success'],
            wait: 500,
            retry: 1500
          },
          ajax_success: {
            primary: 'success',
            fallback: ['observer', 'delayed'],
            wait: 1500,
            retry: 2000
          },
          custom_thankyou: {
            primary: 'custom',
            fallback: ['success', 'params'],
            wait: 1000,
            retry: 2000
          },
          unknown: {
            primary: 'thankYou',
            fallback: ['success', 'observer', 'delayed'],
            wait: 0,
            retry: 2000
          }
        };
        return strategies[behavior] || strategies.unknown;
      }
    };
  
    // ============================================
    // ORDER TRACKING
    // ============================================
  
    const OrderTracking = {
      /**
       * Track order confirmation using intelligent detection
       */
      track: (pseudoId) => {
        const behavior = BehaviorDetector.detect();
        const strategy = BehaviorDetector.getStrategy(behavior);
  
        utils.log('Using strategy:', strategy.primary);
  
        // Execute primary strategy
        OrderTracking.executePrimary(strategy, pseudoId);
  
        // Setup fallback strategies
        OrderTracking.setupFallbacks(strategy, pseudoId);
      },
  
      /**
       * Execute primary capture strategy
       */
      executePrimary: (strategy, pseudoId) => {
        const execute = () => {
          let data = null;
  
          if (strategy.primary === 'thankYou' || strategy.primary === 'orderStatus') {
            data = OrderTracking.captureFromThankYou();
          } else if (strategy.primary === 'success') {
            data = OrderTracking.captureFromSuccess();
          } else if (strategy.primary === 'custom') {
            data = OrderTracking.captureFromCustom();
          }
  
          if (data) {
            OrderTracking.createLink(data, pseudoId, strategy.primary);
            return true;
          }
          return false;
        };
  
        if (strategy.wait > 0) {
          setTimeout(execute, strategy.wait);
        } else {
          execute();
        }
      },
  
      /**
       * Setup fallback capture strategies
       */
      setupFallbacks: (strategy, pseudoId) => {
        // Mutation observer for dynamic content
        if (strategy.fallback.includes('observer')) {
          OrderTracking.setupObserver(pseudoId);
        }
  
        // Delayed retry
        if (strategy.fallback.includes('success') || strategy.fallback.includes('delayed')) {
          setTimeout(() => {
            const data = OrderTracking.captureFromSuccess();
            if (data) OrderTracking.createLink(data, pseudoId, 'fallback');
          }, strategy.retry);
        }
      },
  
      /**
       * Capture from Thank You page
       */
      captureFromThankYou: () => {
        const isThankYou = window.location.pathname.includes('/thank') ||
          window.location.pathname.includes('/orders/') ||
          window.Shopify?.checkout;
  
        if (!isThankYou) return null;
  
        utils.log('Thank You page detected');
        return window.Shopify?.checkout || window.ShopifyAnalytics?.meta || null;
      },
  
      /**
       * Capture from success message (no redirect)
       */
      captureFromSuccess: () => {
        const selectors = '.order-confirmation, .checkout-success, .thank-you-message, .order-complete';
        const element = document.querySelector(selectors);
  
        if (!element) return null;
  
        utils.log('Success message detected');
        return {
          order_id: OrderTracking.extractOrderId(),
          email: OrderTracking.extractEmail(),
          total_price: OrderTracking.extractTotal()
        };
      },
  
      /**
       * Capture from custom thank you page
       */
      captureFromCustom: () => {
        const params = new URLSearchParams(window.location.search);
        const orderId = params.get('order_id') || params.get('order');
  
        if (!orderId) return null;
  
        utils.log('Custom thank you page detected');
        return {
          order_id: orderId,
          email: OrderTracking.extractEmail(),
          total_price: OrderTracking.extractTotal(),
          customer_id: params.get('customer_id')  // Internal use
        };
      },
  
      /**
       * Extract order ID from page
       */
      extractOrderId: () => {
        // Method 1: Text search
        const match = document.body.innerText.match(/#?(\d{4,})/);
        if (match) return match[1];
  
        // Method 2: Meta tag
        const meta = document.querySelector('meta[name="order-id"]');
        if (meta) return meta.content;
  
        // Method 3: Data attribute
        const elem = document.querySelector('[data-order-id]');
        return elem?.dataset.orderId || null;
      },
  
      /**
       * Extract email from page
       */
      extractEmail: () => {
        const regex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
        const elem = document.querySelector('.customer-email, .order-email, [data-customer-email]');
  
        if (elem) {
          const match = elem.innerText.match(regex);
          if (match) return match[1];
        }
  
        return null;
      },
  
      /**
       * Extract total price from page
       */
      extractTotal: () => {
        const elem = document.querySelector('.order-total, .total-price, [data-order-total]');
        if (elem) {
          const price = elem.innerText.replace(/[^0-9.]/g, '');
          return parseFloat(price) || null;
        }
        return null;
      },
  
      /**
       * Setup mutation observer for dynamic content
       */
      setupObserver: (pseudoId) => {
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === 1 && node.classList?.contains('order-confirmation')) {
                utils.log('✅ Success message appeared in DOM');
                setTimeout(() => {
                  const data = OrderTracking.captureFromSuccess();
                  if (data) OrderTracking.createLink(data, pseudoId, 'observer');
                }, 500);
              }
            }
          }
        });
  
        observer.observe(document.body, { childList: true, subtree: true });
      },
  
      /**
       * Create identity link
       */
      createLink: (orderData, pseudoId, source) => {
        if (!orderData) return;
  
        // Prevent duplicates
        const tracked = JSON.parse(utils.storage.get(CONFIG.trackedOrdersKey) || '[]');
        const orderId = orderData.order_id || orderData.id;
  
        if (orderId && tracked.includes(orderId)) {
          utils.log('⚠️ Order already tracked:', orderId);
          return;
        }
  
        const firstTouch = Attribution.getFirstTouch();
  
        const link = {
          event_name: 'identity_link',
          link_type: 'purchase',
          capture_source: source,
          timestamp: new Date().toISOString(),
  
          // Identity
          pseudo_id: pseudoId,
          customer_id: orderData.customer_id || orderData.customerId,  // Internal
          shopify_customer_id: orderData.customer_id || orderData.customerId,  // For GA4
          email: orderData.email,
          email_hash: utils.hash(orderData.email),
  
          // Order details
          order_id: orderId,
          order_number: orderData.order_number || orderData.orderNumber,
          total_price: orderData.total_price || orderData.totalPrice,
          currency: orderData.currency || 'USD',
  
          // Attribution
          first_touch_source: firstTouch?.utm_source,
          first_touch_medium: firstTouch?.utm_medium,
          first_touch_campaign: firstTouch?.utm_campaign,
          first_click_id: firstTouch?.click_id
        };
  
        // Store locally
        utils.storage.set('latest_identity_link', JSON.stringify(link));
  
        if (orderId) {
          tracked.push(orderId);
          utils.storage.set(CONFIG.trackedOrdersKey, JSON.stringify(tracked));
        }
  
        // Send to GA4
        if (typeof gtag === 'function') {
          gtag('event', 'identity_link', {
            pseudo_id: link.pseudo_id,
            shopify_customer_id: link.customer_id,  // Use shopify_customer_id for GA4
            link_type: 'purchase',
            order_id: link.order_id,
            total_price: link.total_price,
            first_touch_campaign: link.first_touch_campaign,
            capture_source: source
          });
  
          if (link.customer_id) {
            gtag('set', 'user_id', link.customer_id.toString());
          }
        }
  
        // Send to dataLayer
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'identity_link', identityData: link });
        }
  
        // Expose globally
        window.IDENTITY_LINK = link;
        utils.log('Identity link created!', link);
      }
    };
  
    // ============================================
    // CUSTOMER IDENTITY LINKING
    // ============================================
  
    const CustomerIdentity = {
      /**
       * Link customer identity on login
       */
      link: (pseudoId) => {
        if (!window.shopifyCustomerData?.isLoggedIn) return;
  
        const customer = window.shopifyCustomerData;
        const firstTouch = Attribution.getFirstTouch();
  
        const link = {
          event_name: 'identity_link',
          link_type: 'login',
          timestamp: new Date().toISOString(),
  
          pseudo_id: pseudoId,
          customer_id: customer.customerId,  // Internal
          shopify_customer_id: customer.customerId,  // For GA4
          email: customer.email,
          email_hash: utils.hash(customer.email),
  
          first_touch_source: firstTouch?.utm_source,
          first_touch_campaign: firstTouch?.utm_campaign
        };
  
        utils.storage.set('customer_identity_link', JSON.stringify(link));
  
        if (typeof gtag === 'function') {
          gtag('event', 'identity_link', {
            pseudo_id: link.pseudo_id,
            shopify_customer_id: link.customer_id,  // Use shopify_customer_id for GA4
            link_type: 'login'
          });
        }
  
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'identity_link', identityData: link });
        }
  
        utils.log('Customer identity linked (login)');
      }
    };
  
    // ============================================
    // INITIALIZATION
    // ============================================
  
    function initialize() {
      utils.log('Initializing visitor tracking v');
  
      // Get or create pseudo_id
      const pseudoId = PseudoID.get();
  
      // Capture attribution
      const attribution = Attribution.capture();
      if (attribution) {
        Attribution.storeFirstTouch(attribution);
      }
  
      // Expose tracking data globally
      window.TRACKING_DATA = {
        pseudo_id: pseudoId,
        first_touch: Attribution.getFirstTouch(),
        current_attribution: attribution,
        initialized: true
      };
  
      // Configure GA4
      if (typeof gtag === 'function') {
        GA4.configure(pseudoId);
      } else {
        // Retry after page load
        window.addEventListener('load', () => {
          setTimeout(() => GA4.configure(pseudoId), 1000);
        });
      }
  
      // Send ad click event if attribution exists
      if (attribution) {
        setTimeout(() => {
          GA4.sendEvent('ad_click', {
            click_source: attribution.fbclid ? 'facebook' :
              attribution.gclid ? 'google' :
                attribution.ttclid ? 'tiktok' : 'utm_only',
            campaign: attribution.utm_campaign,
            medium: attribution.utm_medium,
            source: attribution.utm_source,
            click_id: attribution.click_id
          });
        }, 500);
      }
  
      // Link customer identity if logged in
      setTimeout(() => CustomerIdentity.link(pseudoId), 500);
  
      // Track order confirmation
      setTimeout(() => OrderTracking.track(pseudoId), 1000);
  
      utils.log('Tracking initialized successfully', window.TRACKING_DATA);
    }
  
    // ============================================
    // RUN INITIALIZATION
    // ============================================
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize);
    } else {
      initialize();
    }
  
    // ============================================
    // PUBLIC API
    // ============================================
  
    window.VisitorTracking = {
      getPseudoId: PseudoID.get,
      getFirstTouch: Attribution.getFirstTouch,
      sendEvent: GA4.sendEvent,
      config: CONFIG
    };
  
    utils.log('Visitor Tracking API ready');
  
  })();
  